/**
 * The attendee preview's pure functions and copy.
 *
 * Everything a test needs to hold lives here rather than in a component body: **there is no DOM
 * renderer in this repository, so a rule buried in JSX is a rule with no test.**
 * `event-discovery/constants.ts` states the convention and the reason.
 */

/**
 * "Founder, Rooted Health" — the one line the design draws where the API sends two fields.
 *
 * The API keeps `role` and `company` apart (`MEM-025` AC-3 names them separately, and the profile
 * stores them separately), and the comma lives here because it is presentation. Either half may
 * be missing — registration collects both as optional — and the line degrades to whichever is
 * present rather than rendering a stray comma.
 */
export function roleAtCompany(person: {
  readonly role: string | null;
  readonly company: string | null;
}): string | null {
  const parts = [person.role, person.company].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/**
 * The initials tile the design draws where a photo would go.
 *
 * There is no profile photo on a member (`MEM-006` is not built), so this is what every avatar in
 * these screens renders. Two letters, from the first and last name — the prototype's own "MO",
 * "DR", "PN".
 */
export function initialsOf(person: {
  readonly firstName: string;
  readonly lastName: string;
}): string {
  const first = person.firstName.trim().charAt(0);
  const last = person.lastName.trim().charAt(0);
  return `${first}${last}`.toUpperCase();
}

/** The person's name as one string, for a heading and for an `aria-label`. */
export function fullName(person: {
  readonly firstName: string;
  readonly lastName: string;
}): string {
  return `${person.firstName} ${person.lastName}`.trim();
}

/**
 * `MEM-011` AC-5's notice, split into the two lines the design draws it as.
 *
 * The API composes the whole sentence from the count so the number and the explanation cannot
 * disagree — *"12 other attendees are private. They chose not to appear in the preview."* The
 * prototype draws it as a bold headline over a muted second line, and that split is presentation,
 * so it is a function here rather than a `.split()` inside JSX where nothing could test it.
 *
 * A notice with no second sentence yields an empty `detail` rather than an undefined one, so the
 * component renders one line rather than a line and a gap.
 */
export function privateAttendeesLines(notice: string): {
  readonly headline: string;
  readonly detail: string;
} {
  const boundary = notice.indexOf('. ');
  if (boundary === -1) return { headline: notice, detail: '' };
  return {
    headline: notice.slice(0, boundary + 1),
    detail: notice.slice(boundary + 2),
  };
}

/**
 * The sidebar's way to the whole roster — `Web App.dc.html` §2.2's *"See all {going} attendees"*,
 * counted by the same going figure the module's heading carries (`MEM-011` AC-3), so the button
 * and the heading cannot name two different numbers.
 */
export function seeAllAttendeesLabel(goingCount: number): string {
  return goingCount === 1 ? 'See the 1 attendee' : `See all ${String(goingCount)} attendees`;
}

/**
 * **An open roster with nobody on it** — the defect Sumit reported on 2026-09-11, stated as copy.
 *
 * The module drew its heading, the going count, and then nothing at all: an open roster whose
 * `attendees` and `privateAttendees` are both empty rendered an empty `<ul>` and suppressed the
 * *See all* link, leaving a heading and a number over blank space.
 *
 * It is a real state and not only a bug in the rendering. **The reader is never a row in their own
 * roster**, so a member who is the first to reserve sees *1 going* and no rows, correctly; so does
 * a member at an event where nobody else has chosen to appear (election (a), `MEM-PRV-008`). Both
 * need a sentence, and the sentence must not say *nobody is coming* — the count above it says
 * otherwise, and the module would be contradicting itself.
 *
 * The plural form says what is true when there are others and none of them appear; the singular
 * says what is true when the member is on their own. Neither is a reprimand and neither asks the
 * member to do anything: appearing in a preview is somebody else's private election.
 */
export function emptyRosterNotice(goingCount: number): string {
  return goingCount <= 1
    ? 'You are the first to reserve. Others appear here as they register and choose to show their profile.'
    : 'Nobody going has chosen to show their profile yet. They appear here if they do.';
}

/**
 * `MEM-013` AC-4's count badge, as a label rather than a bare number.
 *
 * The badge renders the digit; this is its accessible name, so a screen reader is told what the
 * number counts instead of reading "3" beside a heading.
 */
export function meetListCountLabel(count: number): string {
  return count === 1 ? '1 person on your meet list' : `${String(count)} people on your meet list`;
}

/**
 * `MEM-013` AC-3 — *"saving somebody is not connecting to them, and the two states are presented
 * distinctly throughout"*.
 *
 * The two labels are here, side by side and in one file, because "distinctly" is a claim about a
 * pair of strings and a claim about a pair of strings should be testable as one. Saving is
 * something the member does to their own private list ("Save to Meet" → "On your meet list");
 * connecting is a request to another person, and it is not built.
 */
export const MEET_LIST_SAVE_LABEL = 'Save to Meet';
export const MEET_LIST_SAVED_LABEL = 'On your meet list';

/**
 * The same state in a narrower place — `Web App.dc.html` §3.9's attendee card and §2.16's attendee
 * detail, whose saved label is *On list* because the control is one of a **pair** rather than a
 * full-width pill with the row to itself.
 *
 * It is the prototype's own shortening of `MEET_LIST_SAVED_LABEL`, not a second state: both mean
 * *this person is on your meet list*, and `MEM-013` AC-3's distinction from connecting holds for
 * both. Kept here rather than inline so the pair can be asserted together.
 *
 * **`MEET_LIST_REMOVE_LABEL` — *Remove from Meet List* — stood beside these until 2026-09-12.** It
 * was the attendee profile's saved label, and it was the only place in the product where the saved
 * state was written as an instruction to undo rather than as a fact about the list. §2.16 draws
 * the detail's saved control as this toggle, and Sumit asked for §2.16 (*"attendee profile is not
 * cleaned up"*, ruling 64). Removal is not gone — pressing *On list* takes them off, exactly as it
 * does on a card — so `MEM-013` AC-2 is reworded rather than broken, and the constant is deleted
 * rather than left unused for somebody to reintroduce a third saved label from.
 */
export const MEET_LIST_ON_LIST_LABEL = 'On list';

/** What a member reads when the write fails. One sentence, wherever the control is drawn. */
export const MEET_LIST_SAVE_FAILED = 'We could not update your meet list.';

/**
 * `MEM-013` AC-3, which Connect now makes it possible to get wrong.
 *
 * Until 2026-08-23 this file carried `CONNECTING_IS_NOT_BUILT`, naming Connect as an absence
 * because `MEM-032`…`MEM-034` were not built. They are, so the absence is gone and a real Connect
 * control sits beside Save to Meet on the attendee profile (`MEM-032` AC-2's second entry point).
 *
 * What survives from that constant is the sentence that was doing the real work. `MEM-013` AC-3
 * is explicit that **saving somebody is not connecting to them**, and with two controls now side
 * by side that distinction is the thing a member can misread: one is a private bookmark nobody is
 * told about, the other asks a person to decide something. Said once, under both.
 */
export const SAVING_IS_NOT_CONNECTING =
  'Saving somebody to your meet list is private to you — they are not told. Connecting asks them.';

/* -------------------------------------------------------------------------------------------
 * The attendee detail — `Web App.dc.html` §2.16's `personOpen` overlay (`MEM-012`, `MEM-056` AC-3)
 *
 * Every string §2.16 writes into that card, in one place, because two screens draw it: the
 * attendee detail itself and the match detail, which is the same card with the narrative above
 * the quote. A label typed into each of them is the two-tables-one-screen failure
 * `docs/standards/building-from-designs.md` names, one level down.
 * ---------------------------------------------------------------------------------------- */

/** §2.16's pill, shown unless the card was opened from Connections (`pd.showEventTag`). */
export const GOING_TO_THIS_EVENT_LABEL = 'Going to this event';

/**
 * `MEM-013` AC-5's marked entry, said on the card rather than by removing the pill.
 *
 * Not the prototype's — §2.16 has one fixture attendee and nobody cancels in it. It is this
 * repository's own honest statement of a state the product really has, and it stands where the
 * pill stands so the card never says *Going to this event* about somebody who is not.
 */
export const NO_LONGER_COMING_LABEL = 'No longer coming to this event';

/** §2.16's three section labels, verbatim (rendered uppercase by the type style, not by the copy). */
export const HERE_TO_TALK_ABOUT_LABEL = 'Here to talk about';
export const ATTENDEE_ABOUT_LABEL = 'About';
export const ATTENDEE_INTERESTS_LABEL = 'Interests';

/**
 * §2.16's connected box.
 *
 * **The prototype's headline is *"Connected · accepted 2 days ago"* and ours is *Connected*.** The
 * relative time is fixture data: no attendee-profile payload carries when a connection was
 * established, and `MEM-012` AC-6 keeps this surface pre-connection in everything but the
 * relationship flag. A figure that cannot be produced is not a reason to drop the box
 * (`docs/standards/building-from-designs.md`) — the box is the prototype's and stays; the number
 * that would have to be invented is the part that goes.
 */
export const CONNECTED_NOTICE_TITLE = 'Connected';

export function connectedNoticeDetail(firstName: string): string {
  return `You can message ${firstName} any time. Your request and their reply are in Messages.`;
}

/** §2.16's pending box, verbatim. */
export const REQUEST_SENT_NOTICE_TITLE = 'Request sent · awaiting reply';

export function requestSentNoticeDetail(firstName: string): string {
  return `You'll be able to message ${firstName} freely once they accept. We'll let you know.`;
}

/**
 * **What the card says about somebody who has written nothing at all**, and when it says it.
 *
 * §2.16 draws the quote, the About and the interests unconditionally, because its one fixture
 * attendee has all three. Real members do not, and the rule for that is already settled on the
 * connection detail (`connections/constants.ts`, `NOTHING_WRITTEN_ABOUT_THEM`, 2026-09-12): each
 * region renders only when the member wrote it, and **one** sentence stands in when none of the
 * three did. A heading over nothing, or a `—` where a paragraph goes, is a silence the member did
 * not choose.
 *
 * That replaces the standing *"{First} has not written an About yet."* line, which rendered
 * whenever the bio alone was absent — so a member with a conversation starter and six interests
 * still got a sentence about an absence, in the place their profile was supposed to be. Sumit,
 * 2026-09-12: *"attendee profile is not cleaned up"* (ruling 66).
 */
export function nothingWrittenYet(firstName: string): string {
  return `${firstName} has not written an About yet.`;
}

/** The way out of the detail overlay at phone width, where it is a full-screen view. */
export const ATTENDEE_DETAIL_CLOSE_LABEL = 'Close';

/**
 * **`BIO_IS_NOT_BUILT` stood here until 2026-09-12, and what it named had stopped being true.**
 *
 * It read *"This screen does not carry a member's About paragraph yet."* — an accurate statement
 * about our own wiring, standing on two member screens (the attendee profile and the match detail)
 * exactly where information about a person belongs. The gap it described was real: `MEM-007`
 * shipped the bio, the storage carried it, and `PreviewAttendeeRecord` did not, so
 * `toFullAttendeeProfile` hardcoded `bio: null` and every reader landed on the sentence.
 *
 * Sumit found the same voice twice on one day — on the connection detail (*"why are we not showing
 * the about and their interests here like in the prototype?"*) and on the event page's empty
 * description (*"it should rather say, no event description provided or something like that"*).
 * The rule that came out of it: **an empty state describes what the reader is missing, not our
 * plumbing.**
 *
 * So the port carries the bio now — `MEM-012` AC-4 lists it among the things the *reduced* view
 * withholds, which only means anything if AC-1's full view has one — and where a member has
 * written none, the screens say that about **them**. Nothing is generated from the other fields.
 */

/**
 * `MEM-025`'s empty state, for a member with no released set for this event.
 *
 * One sentence for every reason there is nothing to show — no set, an unreleased set, an opted-out
 * member — because the API deliberately returns one answer for all of them, and a screen that
 * distinguished them would leak the release schedule the payload was careful not to.
 */
export const NO_MATCHES_YET_TITLE = 'No matches yet';
export const NO_MATCHES_YET_DESCRIPTION =
  'We send a short list of people to meet shortly before the event. It will appear here when it is ready.';


/**
 * **`MEM-027` AC-3 and AC-4: the set went out, and it is empty.**
 *
 * ## This is a fifth state, and it is the only one the member is entitled to know about
 *
 * The four above — no set, an unreleased set, an opted-out member, an event that is not there —
 * all arrive as `matchSet: null`, and the API collapses them on purpose because the difference
 * between them is the release schedule. **A released, empty set is not one of them.** It is a
 * fact about this member's own set, established and finished, and until `MEM-027` it was shown
 * with the copy above: *"It will appear here when it is ready."* A member whose matching has
 * already run and found nothing was told to keep waiting for something that had already happened.
 * That is the sentence AC-3 calls not telling them the truth.
 *
 * ## AC-4's constraint is on where the sentence points
 *
 * *"…without blaming the member or implying a fault."* The story's own Notes give the failing
 * example — *"We couldn't find anyone for you" reads as a verdict on the member* — and the reason
 * it fails is grammatical rather than tonal: the member is the object of the sentence. Softening
 * it does not help. *"We couldn't find any strong matches for you this time"* is the same
 * sentence with an adjective.
 *
 * So the subject here is **the room**, which is also the true explanation: at a small event, or one
 * where somebody's interests are unusual among the people who came, there is genuinely nobody the
 * platform can honestly introduce them to. That is a fact about who registered, it is nobody's
 * fault, and it is the reason `MEM-027`'s Notes call it *"both kinder and more accurate"*.
 *
 * Three things are deliberately absent. **No apology** — *"Sorry!"* invites the reader to look for
 * what went wrong. **No advice** — *"try completing your profile"* is blame with a task attached,
 * and it is also false, since a completed profile does not conjure attendees. **No promise** —
 * the set is released and will not be recomputed (`MEM-026` AC-1), so *"check back later"* would
 * be untrue.
 *
 * And the member is **not notified** of this screen at all (AC-3): `MatchingService.announceRelease`
 * writes to nobody whose set is empty, so a member reaching here has arrived on their own rather
 * than been sent by a push about nothing.
 */
export const NO_MATCHES_THIS_TIME_TITLE = 'No introductions this time';
export const NO_MATCHES_THIS_TIME_DESCRIPTION =
  'We look for people whose goals and interests line up with yours, and at this event we did not find a match worth putting in front of you. It happens in smaller rooms. You are still going, and the attendee list is there whenever you want a look.';

/**
 * `MEM-025` AC-1's own screen heading, and the sentence beneath the event-scoped list.
 *
 * A function rather than a constant because the count is in it, and because the sentence has to
 * be true of a set of one. It says **what the list is** — people, chosen from goals and interests
 * — and never how good they are: "we picked the 3 best" or "your strongest matches" would be
 * `MEM-024` AC-3's prohibition arriving as prose.
 */
export function matchesIntroSentence(count: number): string {
  const people = count === 1 ? '1 person' : `${String(count)} people`;
  return `We picked ${people} we think you should meet, based on your goals and interests.`;
}
